import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import logoUrl from '../assets/pdf-logo.png';
import { PRODUCT_TYPES } from '../constants';
import { formatPrice, calculateTotalPrice } from './index';

// Mağaza renkleri: logodaki kahverengiler
const PRIMARY_COLOR = [106, 69, 49]; // #6A4531 - logodaki "AY HOME" kahvesi; tablo başlıkları, vurgular
const DARK_COLOR = [43, 26, 17]; // #2B1A11 - logodaki koyu kahve; başlıklar, QR
const LIGHT_BG = [247, 241, 236]; // #F7F1EC - açık krem kutu zeminleri
const GRAY_TEXT = [100, 100, 100];

// Türkçe karakter temizleme fonksiyonu
const cleanTurkishChars = (text) => {
  return (text || '').replace(/[ğ]/g, 'g')
    .replace(/[Ğ]/g, 'G')
    .replace(/[ü]/g, 'u')
    .replace(/[Ü]/g, 'U')
    .replace(/[ş]/g, 's')
    .replace(/[Ş]/g, 'S')
    .replace(/[ı]/g, 'i')
    .replace(/[İ]/g, 'I')
    .replace(/[ö]/g, 'o')
    .replace(/[Ö]/g, 'O')
    .replace(/[ç]/g, 'c')
    .replace(/[Ç]/g, 'C');
};

// ---- Logo ve QR kodlar ----
// Sayfa açılınca bir kez hazırlanır; PDF butona basılınca beklemeden oluşur (tarayıcı yeni sekmeyi engellemesin)
const LOGO_RATIO = 260 / 370; // pdf-logo.png genişlik / yükseklik
const QR_LINKS = [
  { url: 'https://ayhomelotus.com', title: 'WEB SITEMIZ', label: 'ayhomelotus.com' },
  { url: 'https://instagram.com/ayhomelotus', title: 'INSTAGRAM', label: '@ayhomelotus' }
];

let pdfAssets = null;
let pdfAssetsPromise = null;

const loadLogo = async () => {
  try {
    const blob = await (await fetch(logoUrl)).blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error('PDF logosu yüklenemedi:', err);
    return null;
  }
};

const makeQr = async (url) => {
  try {
    return await QRCode.toDataURL(url, {
      margin: 0,
      width: 256,
      errorCorrectionLevel: 'M',
      color: { dark: '#2B1A11', light: '#FFFFFF' }
    });
  } catch (err) {
    console.error('QR kod oluşturulamadı:', err);
    return null;
  }
};

export const loadPdfAssets = () => {
  if (!pdfAssetsPromise) {
    pdfAssetsPromise = Promise.all([loadLogo(), ...QR_LINKS.map(link => makeQr(link.url))])
      .then(([logo, ...qrs]) => {
        pdfAssets = { logo, qrs };
        return pdfAssets;
      });
  }
  return pdfAssetsPromise;
};

if (typeof window !== 'undefined') loadPdfAssets();

// Varlıklar hazırsa PDF'i hemen oluştur, değilse hazır olunca
const withPdfAssets = (fn) => (...args) => (pdfAssets ? fn(...args) : loadPdfAssets().then(() => fn(...args)));

const formatToday = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

// Modern Header çiz (yükseklik ~42mm; içerik 45mm'den başlar)
// options.qr: sağ üstte web sitesi ve Instagram QR kodları (müşteri PDF'leri)
const drawHeader = (doc, title, subTitle = '', options = {}) => {
  const showQr = options.qr && pdfAssets?.qrs?.every(Boolean);

  // Üst ince kahverengi şerit
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, 210, 2.5, 'F');

  // Sol: logo + altında Lotus Office
  const logoH = 27;
  const logoW = logoH * LOGO_RATIO;
  const logoX = 14;
  if (pdfAssets?.logo) {
    doc.addImage(pdfAssets.logo, 'PNG', logoX, 6, logoW, logoH);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Lotus Office', logoX + logoW / 2, 37, { align: 'center' });

  // Orta: marka, belge başlığı, müşteri adı, iletişim
  const textX = logoX + logoW + 8;
  const textMaxW = (showQr ? 146 : 196) - textX;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...PRIMARY_COLOR);
  const brand = 'AY HOME LOTUS';
  const brandSpacing = 1.2;
  doc.setCharSpace(brandSpacing);
  doc.text(brand, textX, 11.5);
  doc.setCharSpace(0);
  const brandWidth = doc.getTextWidth(brand) + brandSpacing * brand.length;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_TEXT);
  doc.text(formatToday(), textX + brandWidth + 3, 11.5);
  doc.setFont('helvetica', 'bold');

  doc.setFontSize(17);
  doc.setTextColor(...DARK_COLOR);
  doc.text(cleanTurkishChars(title).toUpperCase(), textX, 20);

  let infoY = 25.5;
  if (subTitle) {
    doc.setFontSize(10.5);
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(cleanTurkishChars(subTitle).toUpperCase(), textX, 26.5);
    infoY = 31;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Tel: +90 546 698 41 16   |   ayhomelotus@gmail.com', textX, infoY);
  const addressLines = doc.splitTextToSize('Lotus Office, Konak Mah. Izmir Yolu Cad. B Blok, No:81, B/D Nilufer Bursa', textMaxW);
  doc.text(addressLines.slice(0, 2), textX, infoY + 4);

  // Sağ: QR kodlar
  if (showQr) {
    const qrSize = 19;
    const qrXs = [150, 175];
    QR_LINKS.forEach((link, idx) => {
      const x = qrXs[idx];
      doc.setDrawColor(226, 216, 208);
      doc.setLineWidth(0.3);
      doc.roundedRect(x - 1.5, 5.5, qrSize + 3, qrSize + 3, 1.5, 1.5, 'S');
      doc.addImage(pdfAssets.qrs[idx], 'PNG', x, 7, qrSize, qrSize);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...DARK_COLOR);
      doc.text(link.title, x + qrSize / 2, 31.5, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...GRAY_TEXT);
      doc.text(link.label, x + qrSize / 2, 35, { align: 'center' });
    });
  }

  // Alt ayırıcı: ince çizgi + logonun altında kalın vurgu
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.3);
  doc.line(14, 41, 196, 41);
  doc.setLineWidth(1.2);
  doc.line(14, 41, 14 + logoW + 4, 41);

  // Reset
  doc.setLineWidth(0.2);
  doc.setDrawColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
};

// Modern Footer çiz
const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;

  // Alt çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.line(20, pageHeight - 25, 190, pageHeight - 25);

  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Ay Home Lotus - Kaliteli Perde Cozumleri', 105, pageHeight - 18, { align: 'center' });
  doc.text('Bu belge bilgisayar ortaminda olusturulmustur.', 105, pageHeight - 12, { align: 'center' });

  // Sayfa numarası
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(`Sayfa ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`, 190, pageHeight - 12, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

// Metni satırlara böl
const splitTextToLines = (text, maxCharsPerLine) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
};

/** Atölye PDF kutusunda gösterilecek ek satırları oluşturur (not hariç) */
const buildWorkshopOrderExtraLines = (order) => {
  const items = [];

  const pushSpacer = () => {
    if (items.length > 0) items.push({ spacer: true });
  };

  const pushAccessories = (accs) => {
    if (accs.length === 0) return;
    pushSpacer();
    accs.forEach((acc) => {
      const isMetre = acc.unitType === 'metre' || acc.unit === 'metre';
      const detail = isMetre
        ? `${acc.meter || acc.metre || '1'} m x ${acc.quantity || '1'} ad`
        : `${acc.quantity || '1'} ad`;
      items.push({ text: `Aksesuar: ${acc.name} (${detail})`, color: 'black' });
    });
  };

  if (order.productType === 'tul' && (order.altEtekKatlama || order.hasAltEtekKatlama)) {
    items.push({ text: 'Alt Etek Katlama olacak', color: 'primary' });
  }

  // Katlamalı Perde: tür başlığı, motor ve aksesuarlar (mekanizma kutunun ana satırında)
  if (order.productType === 'katlamaliPerde') {
    items.push({ text: 'KATLAMALI PERDE', color: 'primary', bold: true });
    if (order.motorType) {
      const motor = order.motorType.includes('Kumanda') ? order.motorType : `${order.motorType} (kumandasiz)`;
      items.push({ text: `Motor: ${motor}`, color: 'black' });
    }
    pushAccessories(Array.isArray(order.accessories) ? order.accessories.filter((a) => a.name) : []);
  }

  if (order.productType === 'kanat') {
    const ozelName = order.ozelTasarimProductName || order.ozelProductName;
    const hasOzel = (order.hasOzelTasarim || ozelName) && ozelName;
    if (hasOzel) {
      pushSpacer();
      const ozelVariant = order.ozelTasarimProductVariant || order.ozelProductVariant;
      let ozelLine = `Ozel Tasarim: ${ozelName}`;
      if (ozelVariant) ozelLine += ` - ${ozelVariant}`;
      items.push({ text: ozelLine, color: 'primary', bold: true });

      const ozelPile = order.ozelTasarimPileModel || order.ozelPileModel;
      if (ozelPile) {
        items.push({ text: `Pile: ${ozelPile}`, color: 'primary' });
      }

      const dims = [];
      const oWidth = order.ozelTasarimWidth || order.ozelWidth;
      const oHeight = order.ozelTasarimHeight || order.ozelHeight;
      const oQty = order.ozelTasarimQuantity || order.ozelQuantity || '1';
      if (oWidth) dims.push(`En: ${oWidth} cm`);
      if (oHeight) dims.push(`Boy: ${oHeight} cm`);
      const dimPart = dims.length ? dims.join('  ') : '';
      const adetPart = `Adet: ${oQty}`;
      items.push({
        text: dimPart ? `${dimPart}  ${adetPart}` : adetPart,
        color: 'primary',
        bold: true
      });
    }

    const hasAstar = (order.hasAstar || order.astarProductName) && order.astarProductName;
    if (hasAstar) {
      pushSpacer();
      let astarLine = `Astar: ${order.astarProductName}`;
      if (order.astarProductVariant) astarLine += ` - ${order.astarProductVariant}`;
      items.push({ text: astarLine, color: 'black' });
    }

    pushAccessories(Array.isArray(order.kanatAccessories) && order.kanatAccessories.length > 0
      ? order.kanatAccessories.filter((a) => a.name)
      : (Array.isArray(order.accessories) ? order.accessories.filter((a) => a.name) : []));
  }

  return items.map((item) => {
    if (item.spacer) return item;
    return { ...item, text: cleanTurkishChars(item.text) };
  });
};

/** Atölye PDF kutusu yüksekliğini içeriğe göre hesaplar */
// Atölye kutusundaki ek bilgiler (özel tasarım, astar, alt etek, katlamalı, aksesuar) ana ürün satırlarıyla aynı boyutta yazılır
const WORKSHOP_EXTRA_FONT_SIZE = 10;
const WORKSHOP_EXTRA_LINE_HEIGHT = 7;
const WORKSHOP_EXTRA_SPACER = 3;
const WORKSHOP_EXTRA_WRAP = 38; // 10 punto, 90mm kutu için satır başına karakter

const calcWorkshopExtrasHeight = (extraItems) => extraItems.reduce((height, item) => {
  if (item.spacer) return height + WORKSHOP_EXTRA_SPACER;
  const wrapped = splitTextToLines(item.text, WORKSHOP_EXTRA_WRAP);
  return height + Math.min(wrapped.length, 2) * WORKSHOP_EXTRA_LINE_HEIGHT;
}, 0);

const calcWorkshopBoxHeight = (extraItems, noteLines) => {
  const noteLineCount = Math.min(noteLines.length, 3);
  const noteBlockHeight = noteLineCount > 0 ? (extraItems.length > 0 ? 12 : 7) + noteLineCount * 5 : 0;
  const innerHeight = 26 + calcWorkshopExtrasHeight(extraItems) + noteBlockHeight;
  return Math.max(63, 14 + innerHeight + 6);
};

// Müşteri bilgileri kutusu
const drawCustomerInfoBox = (doc, customerInfo, startY) => {
  const cleanName = cleanTurkishChars(customerInfo.name || '');
  const cleanAddress = cleanTurkishChars(customerInfo.address || '');

  // Adresi satırlara böl (maks 60 karakter per satır)
  const addressLines = splitTextToLines(cleanAddress, 60);
  const extraLines = Math.max(0, addressLines.length - 1);
  const boxHeight = 45 + (extraLines * 7);

  // Kutu arka planı
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, startY, 180, boxHeight, 3, 3, 'F');

  // Sol taraf - Müşteri bilgileri
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MUSTERI BILGILERI', 20, startY + 10);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Ad Soyad: ${cleanName}`, 20, startY + 20);
  doc.text(`Telefon: ${customerInfo.phone1 || '-'}`, 20, startY + 28);

  if (customerInfo.tcNo) {
    doc.text(`TC / Vergi No: ${customerInfo.tcNo}`, 100, startY + 28);
  }

  // Adres satırları
  let addressY = startY + 36;
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Adres: ' : '       ';
    doc.text(`${prefix}${line}`, 20, addressY);
    addressY += 7;
  });

  // Sağ taraf - Tarihler
  if (customerInfo.orderDate || customerInfo.deliveryDate) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);

    if (customerInfo.orderDate) {
      const orderDate = customerInfo.orderDate?.toDate ? customerInfo.orderDate.toDate() : new Date(customerInfo.orderDate);
      doc.text(`Siparis: ${orderDate.toLocaleDateString('tr-TR')}`, 190, startY + 20, { align: 'right' });
    }

    if (customerInfo.deliveryDate) {
      const deliveryDate = customerInfo.deliveryDate?.toDate ? customerInfo.deliveryDate.toDate() : new Date(customerInfo.deliveryDate);
      doc.text(`Teslimat: ${deliveryDate.toLocaleDateString('tr-TR')}`, 190, startY + 28, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 8;
};

// Müşteri bilgilendirme notlarını çizer
const drawCustomerNotes = (doc, startY) => {
  // 1. ÖNEMLİ BİLGİLENDİRME
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('ONEMLI BILGILENDIRME', 15, startY);

  // Başlık altı ince çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.3);
  doc.line(15, startY + 2.5, 195, startY + 2.5);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);

  const notes = [
    "Ürünlerde, üretim ve boya partilerine bağlı olarak %3-5 oranında ton farklılıkları görülebilir.",
    "Fon ve tül perde ürünlerimizin 30 derece hassas programda yıkanması, maksimum 400 devirde sıkılması tavsiye edilir. Daha iyi sonuç için sıkma işlemi tamamlanmadan nemli şekilde asılması önerilir.",
    "Plise perdelerin, yıkama sonrası kuruyana kadar en az 3 saat kapalı şekilde bekletilmesi gerekmektedir."
  ];

  let currentY = startY + 7;
  const lineSpacing = 3.6;
  const paragraphSpacing = 1.5;

  notes.forEach((note) => {
    const cleanNote = cleanTurkishChars(note);
    const lines = doc.splitTextToSize(cleanNote, 172);
    lines.forEach((line, index) => {
      if (index === 0) {
        doc.setFillColor(...PRIMARY_COLOR);
        doc.circle(17, currentY - 0.9, 0.6, 'F');
      }
      doc.text(line, 20, currentY);
      currentY += lineSpacing;
    });
    currentY += paragraphSpacing;
  });

  // 2. İPTAL, İADE VE TESLİMAT KOŞULLARI
  currentY += 4;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('IPTAL, IADE VE TESLIMAT KOSULLARI', 15, currentY);

  // Başlık altı ince çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.3);
  doc.line(15, currentY + 2.5, 195, currentY + 2.5);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);

  const legalText = "Sipariş konusu ürünler, müşterinin talep ettiği ölçü, model, renk ve kumaş tercihleri doğrultusunda kişiye özel olarak üretildiğinden, üretim sürecinin başlamasının ardından sipariş iptali, iade veya değişiklik talebinde bulunulamaz. Müşterinin kendi isteği veya kişisel gerekçeleriyle siparişten vazgeçmesi hâlinde, ödenen kapora iade edilmez ve oluşan masraf, zarar ve giderlerin talep edilme hakkı saklıdır. Ayrıca, teslim edilen ürünlerin teslimat anında kontrol edilmesi müşterinin sorumluluğundadır. Herhangi bir hata, eksiklik veya ayıp bulunması durumunda, durumun teslimat günü içerisinde firmaya yazılı olarak bildirilmesi gerekmektedir. Aksi hâlde ürünler teslim alınmış ve onaylanmış kabul edilir.";

  const cleanLegal = cleanTurkishChars(legalText);
  const legalLines = doc.splitTextToSize(cleanLegal, 180); // 180mm yazma genişliği

  currentY += 7;
  legalLines.forEach((line) => {
    doc.text(line, 15, currentY);
    currentY += lineSpacing;
  });

  return currentY;
};

// Toplam tutar kutusu - Sağda alt alta format
const drawTotalBox = (doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, startY, showPaymentInfo = false, paidAmount = 0, remainingAmount = 0, showDetailedPricing = true, manualGrandTotal = null) => {
  const vYuzde = parseFloat(vadeliYuzde) || 0;
  const gIskonto = parseFloat(genelIskontoYuzde) || 0;

  const hasVade = vYuzde > 0;
  const hasIskonto = gIskonto > 0;
  const hasProductDiscount = discountedTotalAmount !== totalAmount && discountedTotalAmount > 0;

  // Hesaplamalar
  const vadeliToplam = totalAmount + (totalAmount * vYuzde / 100);
  const iskontoluToplam = totalAmount - (totalAmount * gIskonto / 100);

  // Hiyerarşi (Genel Toplam): Manuel Giriş > Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam
  let grandTotal = totalAmount;
  if (manualGrandTotal !== null && manualGrandTotal !== undefined && manualGrandTotal !== '') {
    grandTotal = parseFloat(manualGrandTotal);
  } else if (hasVade) {
    grandTotal = vadeliToplam;
  } else if (hasIskonto) {
    grandTotal = iskontoluToplam;
  } else if (hasProductDiscount) {
    grandTotal = discountedTotalAmount;
  }

  // Kutu yüksekliğini hesapla
  let itemCount = 1; // Sadece Genel toplam (alt sınır)
  if (showDetailedPricing) {
    itemCount += 1; // Ara toplam
    if (hasProductDiscount) itemCount++;
    if (hasIskonto) itemCount += 2; // % + tutar
    if (hasVade) itemCount += 2; // % + tutar
  }

  if (showPaymentInfo) {
    itemCount += 2; // Ödenen + Kalan
  }
  const boxHeight = 20 + (itemCount * 10);

  // Ana kutu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(100, startY, 95, boxHeight, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let lineY = startY + 12;

  if (showDetailedPricing) {
    // Ara Toplam (Brüt)
    doc.text('Ara Toplam:', 105, lineY);
    doc.text(`${formatPrice(totalAmount)} TL`, 190, lineY, { align: 'right' });
    lineY += 9;

    // Ürün İndirimli Toplam
    if (hasProductDiscount) {
      doc.text('Urun Indirimli Toplam:', 105, lineY);
      doc.text(`${formatPrice(discountedTotalAmount)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Genel İskonto
    if (hasIskonto) {
      doc.text(`Genel Iskonto Tutari (%${genelIskontoYuzde}):`, 105, lineY);
      doc.text(`-${formatPrice(totalAmount * gIskonto / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Iskontolu Toplam:', 105, lineY);
      doc.text(`${formatPrice(iskontoluToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Vade Farkı
    if (hasVade) {
      doc.text(`Vade Farki (%${vadeliYuzde}):`, 105, lineY);
      doc.text(`+${formatPrice(totalAmount * vYuzde / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Vadeli Toplam:', 105, lineY);
      doc.text(`${formatPrice(vadeliToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Ayırıcı çizgi (sadece detaylı görünümde ara toplamlar varsa)
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);
  }


  // Genel toplam
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('GENEL TOPLAM (Net):', 105, lineY + 6);
  doc.text(`${formatPrice(grandTotal)} TL`, 190, lineY + 6, { align: 'right' });

  if (manualGrandTotal !== null && manualGrandTotal !== undefined && manualGrandTotal !== '') {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('(Elle Duzeltilmis)', 105, lineY + 11);
  }

  lineY += 14;

  // Ödenen ve Kalan bilgileri (sadece Müşteri PDF için)
  if (showPaymentInfo) {
    // Ayırıcı çizgi
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    // Ödenen
    doc.text('Odenen:', 105, lineY + 4);
    doc.text(`${formatPrice(paidAmount)} TL`, 190, lineY + 4, { align: 'right' });
    lineY += 9;

    // Kalan
    const isPaymentComplete = Math.abs(remainingAmount) < 0.01 || remainingAmount <= 0;
    doc.setFont('helvetica', 'bold');
    doc.text('Kalan:', 105, lineY + 4);
    if (isPaymentComplete) {
      doc.text('ODEME TAMAMLANDI', 190, lineY + 4, { align: 'right' });
    } else {
      doc.text(`${formatPrice(remainingAmount)} TL`, 190, lineY + 4, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 10;
};

// Müşteri PDF tablo verisini oluşturur
const buildCustomerTableData = (orders, includeMeasurements = true) => {
  const head = includeMeasurements
    ? [['Urun Tipi', 'Oda', 'Urun Adi', 'Olculer(cm)', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)', 'Indirimli (TL)']]
    : [['Urun Tipi', 'Oda', 'Urun Adi', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)', 'Indirimli (TL)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    let cleanPileModel = cleanTurkishChars(order.pileModel || '');
    const discountRate = parseFloat(order.discount) || 0;

    // Plise için özel satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const cleanProductName = item.multisistem ? (p1Base + p2Base + cleanTurkishChars(ms)) : p1Base;

        const row = [
          cleanProductType,
          cleanRoomName,
          cleanProductName
        ];

        if (includeMeasurements) {
          row.push(`${item.width || '-'} x ${item.height || '-'}`);
        }

        const rowTotal = parseFloat(item.rowTotal) || 0;
        const discountedRowTotal = rowTotal * (1 - discountRate / 100);

        row.push(
          '-', // Plise için pile modeli yok
          item.quantity || '1',
          formatPrice(rowTotal),
          discountRate > 0 ? formatPrice(discountedRowTotal) : '-'
        );

        return row;
      });
    }

    // Diğer ürün tipleri için tek satır
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    let nameSuffix = '';
    if (order.productType === 'tul' && order.hasAltEtekKatlama) {
      nameSuffix += ' (Alt Etek Katlama)';
    }
    if (order.direction) {
      nameSuffix += ` (${order.direction})`;
    }

    let cleanProductName = cleanTurkishChars(productName + nameSuffix);
    cleanPileModel = cleanTurkishChars(order.pileModel || '');
    let cleanMeasurements = `${order.width || ''} x ${order.height || ''}`;
    let cleanQuantity = `${order.quantity || '1'}`;

    const subDetailsLines = [];

    // Tül Aksesuarları
    if (order.productType === 'tul' && Array.isArray(order.kanatAccessories) && order.kanatAccessories.length > 0) {
      const accText = order.kanatAccessories.map((a) => {
        const detail = (includeMeasurements && a.unitType === 'metre')
          ? `${a.meter} m x ${a.quantity || 1} Adet`
          : `${a.quantity || 1} Adet`;
        return `${a.name} (${detail} - ${formatPrice(a.total)} TL)`;
      }).join(', ');
      subDetailsLines.push(cleanTurkishChars(`Aksesuarlar: ${accText}`));
    }

    // Kanat Özel Tasarım ve Aksesuarlar
    if (order.productType === 'kanat') {
      if (order.ozelTasarimProductName || order.hasOzelTasarim) {
        const ozelName = order.ozelTasarimProductName || 'Özel Kanat';
        const variantText = order.ozelTasarimProductVariant ? ` (${order.ozelTasarimProductVariant})` : '';
        const sizeText = includeMeasurements && order.ozelTasarimWidth && order.ozelTasarimHeight ? ` | ${order.ozelTasarimWidth}x${order.ozelTasarimHeight} cm` : '';
        const pileModelText = order.ozelTasarimPileModel ? ` | Pile: ${order.ozelTasarimPileModel}` : '';

        let ozelTotal = 0;
        if (order.ozelTasarimTotalPrice && parseFloat(order.ozelTasarimTotalPrice) > 0) {
          ozelTotal = parseFloat(order.ozelTasarimTotalPrice);
        } else if (order.ozelTasarimUsedM2 && order.ozelTasarimUnitPrice) {
          const ozelPrice = calculateTotalPrice(
            order.ozelTasarimUsedM2,
            order.ozelTasarimUnitPrice,
            order.ozelTasarimExtraPrice || 0,
            order.ozelTasarimPileModel,
            order.ozelTasarimCapsulePrice || 0
          );
          const ozelQuantity = parseFloat(order.ozelTasarimQuantity) || 1;
          ozelTotal = ozelPrice * ozelQuantity;
        }

        const cdText = `Ozel Tasarim: ${ozelName}${variantText}${sizeText}${pileModelText} | ${order.ozelTasarimQuantity || 1} Adet${ozelTotal > 0 ? ` | Tutar: ${formatPrice(ozelTotal)} TL` : ''}`;
        subDetailsLines.push(cleanTurkishChars(cdText));
      }
      if (order.astarProductName || order.hasAstar) {
        const astarName = order.astarProductName || 'Astar';
        const variantText = order.astarProductVariant ? ` (${order.astarProductVariant})` : '';
        const priceText = order.astarPrice && parseFloat(order.astarPrice) > 0 ? ` | Tutar: ${formatPrice(order.astarPrice)} TL` : '';
        subDetailsLines.push(cleanTurkishChars(`Astar: ${astarName}${variantText}${priceText}`));
      }

      if (Array.isArray(order.kanatAccessories) && order.kanatAccessories.length > 0) {
        const accText = order.kanatAccessories.map((a) => {
          const detail = (includeMeasurements && a.unitType === 'metre')
            ? `${a.meter} m x ${a.quantity || 1} Adet`
            : `${a.quantity || 1} Adet`;
          return `${a.name} (${detail} - ${formatPrice(a.total)} TL)`;
        }).join(', ');
        subDetailsLines.push(cleanTurkishChars(`Aksesuarlar: ${accText}`));
      }
    }

    const row = [
      cleanProductType,
      cleanRoomName,
      cleanProductName
    ];

    if (includeMeasurements) {
      row.push(cleanMeasurements);
    }

    row.push(
      cleanPileModel,
      cleanQuantity,
      formatPrice(order.totalPrice || 0),
      order.discountedPrice && parseFloat(order.discount) > 0 ? formatPrice(order.discountedPrice) : '-'
    );

    // Ray Sistemi (Müşteri PDF): yalnızca en (ve varsa boy); kullanılan metre / ölçü tamamlama gösterilmez
    if (order.productType === 'raySistemi' && includeMeasurements) {
      row[3] = order.height ? `${order.width || ''} x ${order.height}` : `${order.width || ''}`;
      row[4] = '-';
    }

    // Rustik için detayları göster
    if (order.productType === 'rustik' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
      row[3] = `En: ${order.width || ''} cm\nKul: ${usedCm}`;

      let ayakDetay = cleanTurkishChars(order.ayakModel || '-');
      if (order.ayakSayisi) {
        ayakDetay += `\nAdet: ${order.ayakSayisi}`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
      }
      if (order.ayakCap || order.ayakRenkKodu) {
        ayakDetay += `\n(${order.ayakCap || ''} ${cleanTurkishChars(order.ayakRenkKodu || '')})`;
      }
      row[4] = ayakDetay;
    }

    const rows = [row];
    if (subDetailsLines.length > 0) {
      const totalCols = includeMeasurements ? 8 : 7;
      rows.push([
        {
          content: subDetailsLines.join('\n'),
          colSpan: totalCols,
          styles: {
            halign: 'left',
            fillColor: [248, 250, 252],
            fontStyle: 'italic',
            textColor: [71, 85, 105],
            fontSize: 8.5,
            cellPadding: 4
          }
        }
      ]);
    }

    return rows;
  });

  return { head, body };
};

// Ana Müşteri PDF belgesini oluşturur (açma/paylaşma ayrı)
const buildCustomerPDFDoc = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, options = {}) => {
  const manualGrandTotal = options.manualGrandTotal || null;
  const includeMeasurements = options.includeMeasurements !== false;
  const title = options.title || 'Musteri Siparisi';
  const showPaymentInfo = options.showPaymentInfo || false;
  const showDetailedPricing = options.showDetailedPricing !== false; // Varsayılan true
  const paidAmount = options.paidAmount || 0;
  const remainingAmount = options.remainingAmount || 0;

  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return null;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');
  doc.setProperties({ title: `${cleanTurkishChars(title)} - ${cleanTurkishChars(customerInfo.name || '')}` });

  // Header
  drawHeader(doc, title, '', { qr: true });

  // Müşteri bilgileri
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş tablosu başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY);
  currentY += 8;

  // Tablo verilerini hazırla
  const { head, body } = buildCustomerTableData(orders, includeMeasurements);

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head,
    body,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: includeMeasurements
      ? {
        0: { cellWidth: 22 },  // Urun Tipi
        1: { cellWidth: 18 },  // Oda
        2: { cellWidth: 25 },  // Urun Adi
        3: { cellWidth: 20 },  // Olculer(cm)
        4: { cellWidth: 25 },  // Pile Modeli
        5: { cellWidth: 12, halign: 'center' },  // Adet
        6: { cellWidth: 28, halign: 'right' },   // Toplam Ucret
        7: { cellWidth: 25, halign: 'right' }    // Indirimli
      }
      : {
        0: { cellWidth: 25 },  // Urun Tipi
        1: { cellWidth: 20 },  // Oda
        2: { cellWidth: 30 },  // Urun Adi
        3: { cellWidth: 28 },  // Pile Modeli
        4: { cellWidth: 15, halign: 'center' },  // Adet
        5: { cellWidth: 28, halign: 'right' },   // Toplam Ucret
        6: { cellWidth: 25, halign: 'right' }    // Indirimli
      },
    pageBreak: 'auto',
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, title + ' (Devam)', '', { qr: true });
      }
    }
  });

  // Toplam tutar kutusu
  let finalY = doc.lastAutoTable.finalY + 10;

  // Toplam kutusunun tahmini yüksekliğini hesapla
  const hasVadeli = vadeliYuzde && parseFloat(vadeliYuzde) > 0;
  let estimatedBoxItems = 1; // Genel toplam
  if (showDetailedPricing) {
    estimatedBoxItems += 1; // Ara toplam
    if (discountedTotalAmount !== totalAmount) estimatedBoxItems++;
    if (vadeliYuzde && parseFloat(vadeliYuzde) > 0) estimatedBoxItems += 2;
    if (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) estimatedBoxItems += 2;
  }
  if (showPaymentInfo) estimatedBoxItems += 2;
  const estimatedBoxHeight = 20 + (estimatedBoxItems * 10);
  // Bilgilendirme notları: 8mm boşluk + ölçülen yükseklik (~55mm) + pay; footer çizgisi sayfa altından 25mm yukarıda
  const notesSpace = options.showInfoNotes ? 8 + 58 : 0;
  const pageHeight = doc.internal.pageSize.height;
  const bottomLimit = options.showInfoNotes ? pageHeight - 28 : pageHeight - 40;

  // Sayfa kontrolü - kutu (+ bilgilendirme notları) sığmıyorsa yeni sayfa
  if (finalY + estimatedBoxHeight + notesSpace > bottomLimit) {
    doc.addPage();
    drawHeader(doc, title, '', { qr: true });
    finalY = 50;
  }

  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, showPaymentInfo, paidAmount, remainingAmount, showDetailedPricing, manualGrandTotal);

  if (options.showInfoNotes) {
    drawCustomerNotes(doc, finalY + estimatedBoxHeight + 8);
  }

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  return doc;
};

// ---- Müşteri PDF'lerini açma / paylaşma ----

// Dosya adı için güvenli metin: Türkçe karakterler sadeleşir, boşluklar alt çizgi olur
const toSafeFileName = (value) => (
  cleanTurkishChars(String(value || ''))
    .replace(/[^\w\s.-]+/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
);

// "Ayşe Demir" + teklif -> "Ayse_Demir_Ay_Home_Teklif_Formu.pdf"
export const buildCustomerPdfFileName = (customerName, kind) => {
  const safeName = toSafeFileName(customerName) || 'Musteri';
  const formName = kind === 'teklif' ? 'Teklif_Formu' : 'Siparis_Formu';
  return `${safeName}_Ay_Home_${formName}.pdf`;
};

const isMobileOrTablet = () => {
  const isMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isTouchTablet = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return isMobileUa || isTouchTablet;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// Mobil/tablet: müşteri adıyla indirir. Masaüstü: başlığında dosya adı ve "İndir" butonu olan önizleme sekmesi.
const openOrDownloadPDF = (doc, fileName) => {
  const pdfBlob = doc.output('blob');
  if (isMobileOrTablet()) {
    downloadBlob(pdfBlob, fileName);
    return;
  }
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const viewer = window.open('', '_blank');
  if (!viewer) {
    downloadBlob(pdfBlob, fileName);
    return;
  }
  const safeTitle = fileName.replace(/[<>&"']/g, '');
  viewer.document.open();
  viewer.document.write(`<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>
    html, body { margin: 0; height: 100%; background: #3f3f46; font-family: system-ui, -apple-system, sans-serif; }
    .bar { position: fixed; inset: 0 0 auto 0; z-index: 10; height: 52px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 0 16px; background: #18181b; color: #fff; box-sizing: border-box; }
    .bar span { font-size: 13px; opacity: 0.9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .bar a { flex-shrink: 0; color: #fff; text-decoration: none; background: #ea580c; padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; }
    iframe { border: 0; width: 100%; height: calc(100% - 52px); margin-top: 52px; }
  </style>
</head>
<body>
  <div class="bar"><span>${safeTitle}</span><a download="${safeTitle}" href="${pdfUrl}">İndir</a></div>
  <iframe src="${pdfUrl}" title="${safeTitle}"></iframe>
</body>
</html>`);
  viewer.document.close();
};

// PDF dosyasını (link değil) paylaşım menüsüyle paylaşır; WhatsApp'ta müşteri adlı belge olarak gider.
// Dosya paylaşımı desteklenmiyorsa müşteri adıyla indirir. Dönüş: 'shared' | 'downloaded'
const sharePdfFile = async (doc, fileName) => {
  const blob = doc.output('blob');
  const file = typeof File === 'function' ? new File([blob], fileName, { type: 'application/pdf' }) : null;
  if (file && navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
    try {
      // Yalnızca dosya: başlık/metin/url verilirse bazı uygulamalar onu da mesaja ekler
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (err) {
      if (err && err.name === 'AbortError') throw err; // kullanıcı vazgeçti
      // İzin/zamanlama hatasında indirmeye düş
    }
  }
  downloadBlob(blob, fileName);
  return 'downloaded';
};

const CUSTOMER_PDF_OPTIONS = {
  teklif: (manualGrandTotal) => ({
    includeMeasurements: false,
    title: 'Teklif Formu',
    showPaymentInfo: false,
    showDetailedPricing: true, // Teklif formunda detaylı fiyat göster
    manualGrandTotal,
    showInfoNotes: true
  }),
  siparis: (manualGrandTotal, paidAmount, remainingAmount) => ({
    includeMeasurements: true,
    title: 'Siparis Formu',
    showPaymentInfo: true,
    showDetailedPricing: false, // Sipariş formunda detaylı fiyat gizle
    paidAmount,
    remainingAmount,
    manualGrandTotal,
    showInfoNotes: true
  })
};

// Müşteri (Sipariş Formu) PDF'ini paylaş
export const shareCustomerPDF = withPdfAssets((customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0, manualGrandTotal = null) => {
  const doc = buildCustomerPDFDoc(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, CUSTOMER_PDF_OPTIONS.siparis(manualGrandTotal, paidAmount, remainingAmount));
  if (!doc) return Promise.resolve(null);
  return sharePdfFile(doc, buildCustomerPdfFileName(customerInfo?.name, 'siparis'));
});

// Müşteri (Teklif Formu) PDF'ini paylaş
export const shareCustomerPreOrderPDF = withPdfAssets((customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal = null) => {
  const doc = buildCustomerPDFDoc(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, CUSTOMER_PDF_OPTIONS.teklif(manualGrandTotal));
  if (!doc) return Promise.resolve(null);
  return sharePdfFile(doc, buildCustomerPdfFileName(customerInfo?.name, 'teklif'));
});

// Müşteri (Sipariş Formu) PDF'ini aç
export const generateCustomerPDF = withPdfAssets((customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0, manualGrandTotal = null) => {
  const doc = buildCustomerPDFDoc(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, CUSTOMER_PDF_OPTIONS.siparis(manualGrandTotal, paidAmount, remainingAmount));
  if (doc) openOrDownloadPDF(doc, buildCustomerPdfFileName(customerInfo?.name, 'siparis'));
});

// Müşteri PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0, manualGrandTotal = null) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Siparis Formu', '', { qr: true });
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, true);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 25 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 }, 5: { cellWidth: 12, halign: 'center' }, 6: { cellWidth: 28, halign: 'right' }, 7: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Siparis Formu (Devam)', '', { qr: true }); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 80 + 66 > pageHeight - 28) { doc.addPage(); drawHeader(doc, 'Siparis Formu', '', { qr: true }); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, true, paidAmount, remainingAmount, false, manualGrandTotal);
  drawCustomerNotes(doc, finalY + 80 + 8);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Müşteri (Sipariş öncesi) PDF oluşturma - en/boy bilgisi olmadan
export const generateCustomerPreOrderPDF = withPdfAssets((customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal = null) => {
  const doc = buildCustomerPDFDoc(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, CUSTOMER_PDF_OPTIONS.teklif(manualGrandTotal));
  if (doc) openOrDownloadPDF(doc, buildCustomerPdfFileName(customerInfo?.name, 'teklif'));
});

// Müşteri (Sipariş öncesi) PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPreOrderPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal = null) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Teklif Formu', '', { qr: true });
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, false);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 20 }, 2: { cellWidth: 30 }, 3: { cellWidth: 28 }, 4: { cellWidth: 15, halign: 'center' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 25, halign: 'right' } },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Teklif Formu (Devam)', '', { qr: true }); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 60 + 66 > pageHeight - 28) { doc.addPage(); drawHeader(doc, 'Teklif Formu', '', { qr: true }); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, false, 0, 0, true);
  drawCustomerNotes(doc, finalY + 60 + 8);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Atölye PDF oluşturma
export const generateWorkshopPDF = withPdfAssets((customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const workshopOrders = orders.filter(order =>
    order.productType === 'tul' || order.productType === 'kanat' || order.productType === 'katlamaliPerde'
  );

  if (workshopOrders.length === 0) {
    alert('Atölye PDF için Tül, Kanat veya Katlamalı Perde ürünü bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header - Müşteri adını büyük başlık olarak yazdır (Talep üzerine)
  drawHeader(doc, 'Atolye Siparisi', customerInfo.name);

  // Siparişler için kutular
  const cleanCustomerName = cleanTurkishChars(customerInfo.name);
  let currentY = 45;
  let currentX = 12; // Daha geniş alan için kenardan başla
  const boxWidth = 90;
  const margin = 6; // Sütunlar arası boşluğu biraz daralt
  let rowMaxHeight = 0;

  workshopOrders.forEach((order) => {
    const cleanNotes = cleanTurkishChars(order.notes || '');
    const extraItems = buildWorkshopOrderExtraLines(order);
    const noteLines = cleanNotes ? splitTextToLines(cleanNotes, 42) : [];
    const boxHeight = calcWorkshopBoxHeight(extraItems, noteLines);

    if (currentY + boxHeight > 270) {
      doc.addPage();
      drawHeader(doc, 'Atolye Siparisi', customerInfo.name);
      currentY = 45;
      currentX = 12;
    }

    // Kutu arka planı
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'F');

    // Kutu kenarlığı
    doc.setDrawColor(...PRIMARY_COLOR);
    doc.setLineWidth(0.5);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'S');

    // Üst şerit - müşteri adı + oda adı
    doc.setFillColor(...PRIMARY_COLOR);
    doc.roundedRect(currentX, currentY, boxWidth, 14, 3, 3, 'F');
    doc.rect(currentX, currentY + 7, boxWidth, 7, 'F'); // Alt köşeleri düzelt

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);

    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    // Sol: Müşteri Adı (Talep üzerine başlık), Sağ: Oda adı
    doc.text(cleanCustomerName.substring(0, 25), currentX + 4, currentY + 9);
    doc.setFontSize(8);
    doc.text(cleanRoomName, currentX + boxWidth - 4, currentY + 9, { align: 'right' });

    // İçerik
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    let fullProductName = order.productName || '';
    if (order.productVariant) fullProductName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(fullProductName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
    // Katlamalı Perde'de pile yok: ikinci satır mekanizma tipidir
    const isKatlamali = order.productType === 'katlamaliPerde';
    const secondLine = isKatlamali
      ? `Mekanizma: ${cleanTurkishChars(order.mechanismType || '-').substring(0, 22)}`
      : `Pile: ${cleanTurkishChars(order.pileModel || '').substring(0, 25)}`;

    doc.text(`Urun: ${cleanProductName.substring(0, 28)}`, currentX + 4, currentY + 20);
    doc.text(secondLine, currentX + 4, currentY + 28);
    doc.setFont('helvetica', 'bold');
    doc.text(`En: ${order.width || '-'} cm  Boy: ${order.height || '-'} cm  Adet: ${order.quantity || '1'}`, currentX + 4, currentY + 36);

    // Ek bilgiler (alt etek katlama, özel tasarım, astar, katlamalı motor, aksesuar)
    let contentY = currentY + 44;
    if (extraItems.length > 0) {
      doc.setFontSize(WORKSHOP_EXTRA_FONT_SIZE);
      doc.setFont('helvetica', 'normal');
      extraItems.forEach((item) => {
        if (item.spacer) {
          contentY += WORKSHOP_EXTRA_SPACER;
          return;
        }
        const textColor = item.color === 'primary' ? PRIMARY_COLOR : [0, 0, 0];
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
        const wrapped = splitTextToLines(item.text, WORKSHOP_EXTRA_WRAP);
        wrapped.slice(0, 2).forEach((wrappedLine) => {
          doc.text(wrappedLine, currentX + 4, contentY);
          contentY += WORKSHOP_EXTRA_LINE_HEIGHT;
        });
      });
      doc.setTextColor(0, 0, 0);
    }

    // Not alanı — ek bilgilerin altında
    if (cleanNotes) {
      if (extraItems.length > 0) contentY += 3;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_TEXT);
      doc.text('Not:', currentX + 4, contentY);
      contentY += 5;
      noteLines.slice(0, 3).forEach((line) => {
        doc.text(line, currentX + 4, contentY);
        contentY += 5;
      });
    }

    doc.setTextColor(0, 0, 0);

    // Sonraki kutu pozisyonu
    rowMaxHeight = Math.max(rowMaxHeight, boxHeight);
    currentX += boxWidth + margin;

    if (currentX + boxWidth > 200) {
      currentX = 12;
      currentY += rowMaxHeight + 10;
      rowMaxHeight = 0;
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
});

// Montaj listesi ölçüsü: "300 x 260", yalnızca en varsa "300", hiçbiri yoksa "-"
const formatInstallationDimensions = (width, height) => {
  const w = width !== undefined && width !== null && String(width).trim() !== '' ? String(width).trim() : '';
  const h = height !== undefined && height !== null && String(height).trim() !== '' ? String(height).trim() : '';
  if (w && h) return `${w} x ${h}`;
  return w || h || '-';
};

// Montaj PDF oluşturma
export const generateInstallationPDF = withPdfAssets((customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Montaj Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş listesi başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MONTAJ LISTESI', 20, currentY);
  currentY += 8;

  // Siparişleri oda gruplarına göre sırala
  const sortedOrders = [...orders].sort((a, b) => {
    const roomGroupA = (a.roomName || '').substring(0, 3).toUpperCase();
    const roomGroupB = (b.roomName || '').substring(0, 3).toUpperCase();

    if (roomGroupA !== roomGroupB) {
      return roomGroupA.localeCompare(roomGroupB, 'tr-TR');
    }

    const roomCompare = (a.roomName || '').localeCompare(b.roomName || '', 'tr-TR');
    if (roomCompare !== 0) return roomCompare;

    return (a.productType || '').localeCompare(b.productType || '', 'tr-TR');
  });

  // Tablo verileri
  const tableData = sortedOrders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');

    // Plise için detaylı satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const namePart = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
        const fullProductName = namePart + cleanTurkishChars(` - ${item.quantity} Adet`);

        return [
          cleanRoomName,
          cleanProductType,
          fullProductName,
          formatInstallationDimensions(item.width, item.height)
        ];
      });
    }

    // Diğer ürünler için
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    // En x Boy: özel tasarımlı kanatta ana ve özel ölçü alt alta; ray/rustikte yalnızca en
    let dimensions;
    if (order.productType === 'kanat' && (order.hasOzelTasarim || order.ozelTasarimProductName)) {
      dimensions = `Ana: ${formatInstallationDimensions(order.width, order.height)}\nOzel: ${formatInstallationDimensions(order.ozelTasarimWidth, order.ozelTasarimHeight)}`;
    } else if (order.productType === 'raySistemi' || order.productType === 'rustik') {
      dimensions = formatInstallationDimensions(order.width, null);
    } else {
      dimensions = formatInstallationDimensions(order.width, order.height);
    }

    return [[
      cleanRoomName,
      cleanProductType,
      cleanProductName,
      dimensions
    ]];
  });

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head: [['Oda', 'Urun Tipi', 'Urun Adi', 'En x Boy (cm)']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 10,
      cellPadding: 5
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 35 },
      2: { cellWidth: 70 },
      3: { cellWidth: 35, halign: 'center', fontStyle: 'bold' }
    },
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, 'Montaj Siparisi (Devam)');
      }
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
});

// Firma PDF oluşturma
export const generateFirmaPDF = withPdfAssets((customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Firma Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Siparişleri ürün tipine göre ayır
  const tulKanatOrders = orders.filter(order => order.productType === 'tul' || order.productType === 'kanat');
  const otherOrders = orders.filter(order => !['tul', 'kanat'].includes(order.productType));

  // Tül ve Kanat tablosu
  if (tulKanatOrders.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('TUL VE KANAT SIPARISLERI', 20, currentY);

    const tulKanatTableData = tulKanatOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

      return [
        cleanProductType,
        cleanProductName,
        `${order.usedM2 || ''} m`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Urun Tipi', 'Urun Adi', 'Kullanilan m', 'Adet']],
      body: tulKanatTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Firma Siparisi (Devam)');
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 15;
  }

  // Diğer ürünler tablosu
  if (otherOrders.length > 0) {
    if (currentY > 200) {
      doc.addPage();
      drawHeader(doc, 'Firma Siparisi (Devam)');
      currentY = 50;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('MEKANIK URUN SIPARISLERI', 20, currentY);

    const otherTableData = otherOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

      let finalProductName = cleanProductName;
      if (order.productType === 'rustik') {
        let ayakDetay = ` [Ayak: ${cleanTurkishChars(order.ayakModel || '')}`;
        if (order.ayakRenkKodu) ayakDetay += ` - ${cleanTurkishChars(order.ayakRenkKodu)}`;
        ayakDetay += ` - ${order.ayakSayisi || 0} Adet`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
        ayakDetay += ']';
        finalProductName += ayakDetay;

        // Aksesuar ve Ray seçeneklerini de ekle
        if (Array.isArray(order.rustikAksesuarlar) && order.rustikAksesuarlar.length > 0) {
          finalProductName += '\n Aksesuarlar: ' + order.rustikAksesuarlar.map(a => `${cleanTurkishChars(a.name)} (${a.quantity})`).join(', ');
        }
        if (Array.isArray(order.rustikRaySecenekleri) && order.rustikRaySecenekleri.length > 0) {
          finalProductName += '\n Ray: ' + order.rustikRaySecenekleri.map(r => `${cleanTurkishChars(r.name)} (${r.quantity})`).join(', ');
        }
      }

      const isRay = order.productType === 'raySistemi';
      const isRustik = order.productType === 'rustik';

      const usedVal = isRay ? (order.usedMeter ? `${order.usedMeter} m` : '-')
        : isRustik ? (order.usedCm ? `${order.usedCm} cm` : '-')
          : (order.usedM2 ? `${order.usedM2} m2` : '-');

      const spec = isRay && order.olcuTamamlama ? `\n(${cleanTurkishChars(order.olcuTamamlama)})` : '';

      return [
        cleanProductType,
        finalProductName,
        `${order.width || ''}`,
        (isRay || isRustik) ? '-' : `${order.height || ''}`,
        `${usedVal}${spec}`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Urun Tipi', 'Urun Adi', 'En (cm)', 'Boy (cm)', 'Kullanilan m2', 'Adet']],
      body: otherTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 50 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Firma Siparisi (Devam)');
        }
      }
    });
  }

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
});
